import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Loader2, CheckCircle, AlertTriangle } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { useAuth } from "@/auth/useAuth"
import { hasPanelAccess } from "@/auth/scopes"
import type { UserJwt } from "@/auth/types"
import { safeError } from "@/utils/safeError"

export const MagicLinkVerification = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { verifyMagicLink, error, clearError, user_jwt } = useAuth()
  const [verificationState, setVerificationState] = useState<
    "verifying" | "success" | "error"
  >("verifying")

  // Where this identity belongs after signing in. Panel access is a scope
  // question, asked the same way everywhere — the hand-rolled
  // `is_staff || is_superuser || roles.includes("admin")` this replaced sent
  // every viewer and operator to the app-download page instead of the panel.
  const landingFor = (jwt: UserJwt) =>
    hasPanelAccess(jwt)
      ? "/admin"
      : jwt.default_redirect === "/my/dashboard"
        ? "/auth/login"
        : jwt.default_redirect

  const token = searchParams.get("token")

  // A failed attempt over a live session is not a failure to show anybody: the
  // redirect below is already taking them somewhere. Without this the failure
  // card renders for a frame first.
  //
  // (Verification is a POST, so link prefetching cannot consume a token —
  // whatever produced the failing attempt, it was not a burned link.)
  // Read the session from STORAGE, not from `isAuthenticated`.
  //
  // That flag is a plain in-memory atom: it starts false on every page load
  // and only flips when this tab performs a sign-in. Land here with a session
  // already on disk and an attempt that fails for any reason, and the flag is
  // still false — so guarding on it showed the failure card to somebody who
  // was demonstrably signed in. Reproduced exactly that way: localStorage
  // holding an unexpired token while the page said the link was malformed.
  //
  // `user_jwt` is derived from the stored token (userJwtSyncAtom), which is
  // what actually decides whether they are logged in.
  const hasStoredSession = Boolean(user_jwt && !user_jwt.is_anon)
  const showFailure = verificationState === "error" && !hasStoredSession

  useEffect(() => {
    if (!token) {
      setVerificationState("error")
      return
    }

    const verify = async () => {
      try {
        const result = await verifyMagicLink(token)

        // A good link on a 2FA-protected account lands here. It is a success
        // that is not finished, not a failure — showing "expired or malformed"
        // for it is what made every admin sign-in look broken.
        if (result.status === "two_factor") {
          navigate("/auth/2fa", { replace: true })
          return
        }
        if (result.status === "failed") {
          setVerificationState("error")
          return
        }

        setVerificationState("success")
        const destination = landingFor(result.user)
        setTimeout(() => navigate(destination, { replace: true }), 2000)
      } catch (error) {
        console.error("Magic link verification failed:", safeError(error))
        setVerificationState("error")
      }
    }

    verify()
  }, [token, verifyMagicLink, navigate])

  // If the session is real, the page has no business claiming otherwise.
  //
  // This used to fire only from "success", so a failed *attempt* rendered
  // "your link is expired or malformed" over a perfectly good session —
  // observed on staging, where the sign-in demonstrably worked and the page
  // said it had not. The attempt and the outcome are different facts, and
  // only the outcome is worth showing anyone.
  useEffect(() => {
    if (hasStoredSession && user_jwt) {
      navigate(landingFor(user_jwt), { replace: true })
    }
  }, [hasStoredSession, verificationState, navigate, user_jwt])

  const handleRetry = () => {
    clearError()
    navigate("/auth/login")
  }

  return (
    <div className="min-h-screen bg-gradient-comfort flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {verificationState === "verifying" && (
            <>
              <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <CardTitle className="text-2xl text-foreground">
                Verifying your login...
              </CardTitle>
              <CardDescription>
                Please wait while we verify your magic link
              </CardDescription>
            </>
          )}

          {verificationState === "success" && (
            <>
              <div className="mx-auto mb-4 w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-success" />
              </div>
              <CardTitle className="text-2xl text-foreground">
                Welcome back!
              </CardTitle>
              <CardDescription>
                You've been signed in successfully. Redirecting to your
                dashboard...
              </CardDescription>
            </>
          )}

          {showFailure && (
            <>
              <div className="mx-auto mb-4 w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-destructive" />
              </div>
              <CardTitle className="text-2xl text-foreground">
                Verification failed
              </CardTitle>
              <CardDescription>
                There was a problem verifying your magic link
              </CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent>
          {showFailure && (
            <div className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {error.message || "Invalid or expired magic link"}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Your magic link may have:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Expired (links are valid for 15 minutes)</li>
                  <li>Already been used</li>
                  <li>Been malformed or incomplete</li>
                </ul>
              </div>

              <Button onClick={handleRetry} className="w-full" size="lg">
                Request a new magic link
              </Button>
            </div>
          )}

          {verificationState === "verifying" && (
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                This should only take a moment...
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
